<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import Textarea from 'primevue/textarea';
import { EMIT } from '@/enum';
import AddFileModal from '@/components/MessageCenter/components/Chat/components/AddFileModal.vue';
import AudioMessage from '@/components/MessageCenter/components/Chat/components/AudioMessage.vue';
import AudioVisualizer from '@/components/MessageCenter/components/Chat/components/AudioVisualizer.vue';
import { pickBestMime, makeFileName } from '@/composables';
import { useMessageCenterStore } from '@/store';

const emit = defineEmits([EMIT.MESSAGE_SENT]);
const messageCenterStore = useMessageCenterStore();
const addFileModalVisible = ref(false);
const BIT_RATES = 128000;

const isRecording = ref<boolean>(false);
const duration = ref<number>(0);
let mediaRecorder: MediaRecorder | null = null;
const mediaStream = ref<MediaStream | null>(null);
let chunks: Blob[] = [];

const recordedFile = ref<File | null>(null);
const audioUrl = ref<string | null>(null);

const toggleAddFileModal = () => (addFileModalVisible.value = !addFileModalVisible.value);
const hasAudioPreview = computed(() => !!recordedFile.value);

const buildObjectUrl = (file: File | null) => {
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
  audioUrl.value = file ? URL.createObjectURL(file) : null;
};

const startRecording = async () => {
  messageCenterStore.messageText = '';
  if (hasAudioPreview.value) discardAudio();

  try {
    mediaStream.value = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    messageCenterStore?.doToast?.('Cannot access microphone.', 'Error', true);
    return;
  }

  const { mime } = pickBestMime();
  try {
    mediaRecorder = new MediaRecorder(mediaStream.value, { mimeType: mime, audioBitsPerSecond: BIT_RATES });
  } catch {
    messageCenterStore?.doToast?.('Cannot start recorder.', 'Error', true);
    mediaStream.value?.getTracks().forEach((t) => t.stop());
    mediaStream.value = null;
    return;
  }

  chunks = [];
  mediaRecorder.ondataavailable = (e) => e.data?.size && chunks.push(e.data);

  mediaRecorder.onstop = () => {
    const type = chunks[0]?.type || mime;
    const blob = chunks.length ? new Blob(chunks, { type }) : null;

    if (!blob) {
      messageCenterStore?.doToast?.('No audio data recorded.', 'Error', true);
    } else {
      const file = new File([blob], makeFileName(blob.type), { type: blob.type });
      recordedFile.value = file;
      buildObjectUrl(file);
    }

    mediaStream.value?.getTracks().forEach((t) => t.stop());
    mediaStream.value = null;
    mediaRecorder = null;
    chunks = [];
  };

  mediaRecorder.start();
  isRecording.value = true;
};

const stopRecording = () => {
  try {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
  } catch {
    console.error('Error stopping the media recorder');
  } finally {
    isRecording.value = false;
  }
};

const recordMessage = async () => (isRecording.value ? stopRecording() : await startRecording());
const sendAction = () => (hasAudioPreview.value ? sendAudio() : sendMessage());

const sendMessage = () => {
  const message = (messageCenterStore.messageText || '').trim();
  if (!message) return;
  const fd = new FormData();
  fd.append('content', message);
  fd.append('type', 'text');
  messageCenterStore.messageText = '';
  emit(EMIT.MESSAGE_SENT, fd);
};

const sendMessageFile = (formData: FormData) => {
  emit(EMIT.MESSAGE_SENT, formData);
  addFileModalVisible.value = false;
};

const sendAudio = () => {
  if (!recordedFile.value) return;
  const formData = new FormData();
  formData.append('content', messageCenterStore.messageText || '');
  formData.append('type', 'file');
  formData.append('attachment[]', recordedFile.value, recordedFile.value.name);

  const duration = Math.floor((recordedFile.value.size * 8) / BIT_RATES);
  const metadata = {
    mime_type: recordedFile.value.type,
    file_size: recordedFile.value.size,
    duration: Number.isFinite(duration) ? duration : null,
  };
  formData.append('metadata', JSON.stringify(metadata));
  messageCenterStore.messageText = '';
  discardAudio();
  emit(EMIT.MESSAGE_SENT, formData);
};

const discardAudio = () => {
  buildObjectUrl(null);
  recordedFile.value = null;
};

const updateDuration = (newDuration: number) => {
  duration.value = newDuration;
};

onBeforeUnmount(() => {
  if (isRecording.value) stopRecording();
  mediaStream.value?.getTracks().forEach((t) => t.stop());
  mediaStream.value = null;
  mediaRecorder = null;
  chunks = [];
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
});
</script>

<template>
  <div class="flex items-center w-full px-2 py-1 gap-2">
    <div class="message-input-box">
      <Icon
        v-if="!hasAudioPreview"
        :name="isRecording ? 'stop' : 'mic'"
        class="audio-control-icon control-icon"
        @click="recordMessage"
      />

      <AudioMessage
        v-if="hasAudioPreview && audioUrl"
        :src="audioUrl"
        :meta-duration="duration"
        @discard="discardAudio"
      />

      <AudioVisualizer
        v-if="isRecording"
        :stream="mediaStream"
        class="flex-1 waveform-canvas"
        @close="updateDuration"
      />

      <Textarea
        v-if="!hasAudioPreview && !isRecording"
        v-model="messageCenterStore.messageText"
        :auto-resize="true"
        type="text"
        placeholder="Message"
        class="flex-1 text-sm"
        @keyup.enter="sendMessage"
      />

      <div class="icon-actions">
        <i v-if="!hasAudioPreview && !isRecording" class="pi pi-plus-circle control-icon" @click="toggleAddFileModal" />
        <Icon v-if="!isRecording" name="send" class="control-icon" @click="sendAction" />
      </div>
    </div>
  </div>

  <AddFileModal v-if="addFileModalVisible" @cancel="toggleAddFileModal" @send-message-file="sendMessageFile" />
</template>

<style scoped lang="scss">
.control-icon {
  font-size: 1.25rem;
  cursor: pointer;
}
.audio-control-icon {
  font-size: 1.5rem;
  cursor: pointer;
  align-self: center;
  justify-self: center;
  border-radius: 50%;
  padding: 0.25rem;
  margin: 0 0.25rem;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}
.icon-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.p-inputtextarea {
  text-align: left;
  align-items: center !important;
  border: none !important;
}
.message-input-box {
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  flex: 1;
  height: 3rem;
  padding-right: 0.5rem;
  border: none;
}
.waveform-canvas {
  width: 95%;
  height: 1.5rem;
  background: #f5f5f5;
  border-top-left-radius: 0%;
  border-bottom-left-radius: 0%;
  border-top-right-radius: 0%;
  border-bottom-right-radius: 4px;
}
</style>
